/**
 * Die Aufnahme-Pipeline aus `ARCHITECTURE.md` 4.6 (Spec 09, „Ziel"): eine
 * signierte Upload-URL holen, die Datei **direkt zum Storage** laden, dabei
 * **parallel** die Textebene lesen (`pdfText.ts`), und erst danach `POST
 * /documents` — **mit** dem gelesenen Text, im selben Aufruf (ADR 0018).
 *
 * „Der Upload beginnt sofort; der Text wird währenddessen gelesen" (Spec 09,
 * „Während der Text gelesen wird"): `readDocumentText` startet, sobald die
 * Datei feststeht, nicht erst nachdem die Upload-URL da ist — beide laufen
 * nebeneinander, `POST /documents` wartet auf beide.
 *
 * Komponiert drei Mutationen aus `api.ts` statt selbst zu `fetch`en — die
 * einzige Ausnahme ist `uploadFileToStorage` (`lib/storage-upload.ts`).
 */

import { useState } from 'react'

import { useCreateDocument, useCreateUploadUrl } from '@/features/documents/api'
import type { DocumentRow } from '@/features/documents/api'
import { buildDocumentCreateBody } from '@/features/documents/document-form'
import type { DocumentFormValues } from '@/features/documents/document-form'
import { readDocumentText } from '@/features/documents/pdfText'
import { uploadFileToStorage } from '@/lib/storage-upload'

interface UploadDocumentArgs {
  readonly file: File
  readonly form: DocumentFormValues
  /** `undefined` heißt Eingang. */
  readonly subjectId: string | undefined
  /** `undefined` heißt kein Absender gewählt (Spec 24c). */
  readonly senderId?: string
}

export function useDocumentUpload() {
  const createUploadUrl = useCreateUploadUrl()
  const createDocument = useCreateDocument()
  const [isUploading, setIsUploading] = useState(false)

  async function upload({ file, form, subjectId, senderId }: UploadDocumentArgs): Promise<DocumentRow> {
    setIsUploading(true)
    try {
      const textPromise = readDocumentText(file)

      const signed = await createUploadUrl.mutateAsync({
        subjectId,
        originalFilename: file.name,
        mimeType: file.type,
        byteSize: file.size,
      })
      await uploadFileToStorage(signed.upload_url, file)

      const text = await textPromise

      return await createDocument.mutateAsync({
        body: buildDocumentCreateBody(
          form,
          { storageKey: signed.storage_key, originalFilename: file.name, mimeType: file.type, byteSize: file.size },
          subjectId,
          text,
          senderId,
        ),
        idempotencyKey: crypto.randomUUID(),
      })
    } finally {
      setIsUploading(false)
    }
  }

  return { upload, isUploading }
}
